/**
 * REWRITTEN — RO-3-00e / #44472.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_hiring/views.tsx`
 * (1,366 LOC). The `app/` copy STAYS (PRD D6 — the Next tree is the rollback).
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * THIS IS A REWRITE, NOT A PORT — and the difference is the whole item.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `docs/rollover/inventory/shared-render.md` §4 lists the origin as the LARGEST
 * single blocked file in the cross-section shared render layer: nine `async`
 * server components that each `await ensureHiringSeed()`, open the vault with
 * `getDb()`, and compose 3–13 `@excellent/core/hiring` loaders IN THE RENDER
 * PATH. §4's verdict — "these cannot be copied into `excellent/client/` at any
 * effort level: the data has to arrive over a contract instead."
 * `shared-render-ownership.md` §6 gives it its own item because it is 1,366 own
 * LOC with **zero private delegates**: one file, one contract surface.
 *
 * So each view below is the SAME render, reading the SAME data, arriving a
 * different way:
 *
 *   server component + N loaders   →   one `useApi(hiringViews.X, …)` read
 *
 * The composition moved server-side into `excellent/server/routes/hiring-views.ts`
 * against `@excellent/ui/api/contracts/hiring-views`. Precedent for the shape is
 * RO-3-02b's `workflowDetail.get` ("the server render becomes ONE contracted
 * read") — one round trip per view, not thirteen.
 *
 * ── The three hazards this class of file walks into, and what closes each ──
 *
 * 1. **Never value-import a server sink.** The origin imports
 *    `@excellent/core/server`, `@excellent/core/hiring` AND the bare
 *    `@excellent/core` barrel. The barrel is not browser-safe
 *    (`index → objectives → events/engine → db → security/{keystore,keychain}`),
 *    and `client/vite.config.ts`'s header records the measured consequence of the
 *    single import this file makes — `HIRING_DISPLAY_NAME` — reaching it:
 *    `security/{keystore,keychain}` + `better-sqlite3` in the browser graph eight
 *    hops down, 30 externalization warnings, exit 0. So NOTHING here imports
 *    `@excellent/core*` at all, at any depth. The product label crosses the WIRE
 *    (`hiringSettingsView`'s `productLabel`), not the import graph. A client
 *    module that value-imports `server-only` makes every route 500 at once, so
 *    that is absent too — see contract B in `../index.ts`.
 *
 * 2. **`notFound()` cannot be thrown here.** Each view renders its own
 *    not-found state instead, so the shell's chrome and the back-link survive — a
 *    bad slug is a wrong turn, not a crash. Same choice `agent-workflow-detail.tsx`
 *    made and for the same reason; the endpoint returns `job: null` rather than a
 *    404 so the distinction stays in the DATA.
 *
 * 3. **A rewritten file gets an allowlist allowance of 0.** The origin holds a
 *    frozen `check-button-consistency` Pass-3 entry (allowance 3). Two of those
 *    repainted Badge with a text-size utility on className (plus a margin), which
 *    forks the atom's own type-subtext base at the call site. Badge has no size prop
 *    to reach for, so the fix is to DROP the repaint and let the atom's canonical
 *    size stand. The badges below carry layout classes only.
 *
 * ── What is deliberately NOT carried over ──
 *
 * `CandidatesView`'s `rawRows` was `buildObjectRowsForKinds(HIRING_KINDS)`, a
 * vault read. `HiringScopeActions` (ported at RO-3-00) already takes `rawRows` as
 * a PROP for exactly this reason: shared-render.md §5 records that the
 * `objects.list` Operation it needs **does not exist yet** and is owed by RO-3-11.
 * Inventing it here would be building another item's contract; supplying it from
 * a `fetch()` would be a rail violation (CLAUDE.md hard rule 1). So `rawRows` is
 * an optional prop defaulting to `[]`, and the route owner (RO-3-10) threads it
 * once the Operation lands. That is the same shape `shared-render-proof.tsx`
 * already uses.
 *
 * `SimulateInline` in `PipelineHubView` renders, but `intelSimulate.options`/
 * `.run` are among the 5 ops RO-1-16 owes a mount for (`shared-render.md` §6), so
 * its calls 404 until then. Contract E in `../index.ts` — not this item's bug.
 *
 * ── Call-site parity ──
 *
 * Every export keeps its origin signature — `{ basePath, searchParams }`,
 * `{ basePath, slug }`, `{ basePath, id }` — including the `searchParams`-shaped
 * bag and the `flat()` reader, so RO-3-10 swaps an import path rather than
 * rewriting nine call sites. `id`/`slug` stay STRINGS, as the Next route params
 * were.
 */

import * as React from "react";
import { Plus } from "@excellent/ui/icons";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Link,
  PageHeader,
  ScopedSearchButton,
  SimulateInline,
  StatusBadge,
  SyncBadge,
  ProductIntegrationsPanel,
  OnThisPageNav,
  type OnThisPageGroup,
  SectionHeading,
  WorkList,
  iconForKind,
  titleCase,
  formatDate,
  formatDateTime,
  formatDayMonth,
  formatNumber,
  objectHref,
  type WorkRowModel,
  type WorkPhase,
} from "@excellent/ui";
import { RouterLink } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { PipelineSettings } from "@excellent/ui/blocks/hiring/pipeline-settings";
import { ScorecardTemplateSettings } from "@excellent/ui/blocks/hiring/scorecard-template-settings";
import { SourceSettings } from "@excellent/ui/blocks/hiring/source-settings";
import { JobForm } from "@excellent/ui/blocks/hiring/job-form";
import { JobActionsBar } from "@excellent/ui/blocks/hiring/job-actions-bar";
import { HiringPipelineBoard } from "@excellent/ui/blocks/hiring/pipeline-kanban-adapter";
import { ApplicationActions } from "@excellent/ui/blocks/hiring/application-actions";
import { ScorecardForm } from "@excellent/ui/blocks/hiring/scorecard-form";
import { ScorecardSummary } from "@excellent/ui/blocks/hiring/scorecard-summary";
import { NoteAddForm } from "@excellent/ui/blocks/hiring/note-add-form";
import type { ApplicationWithJoins } from "@excellent/ui/api/contracts/hiring-applications";
import type { JobStatus } from "@excellent/ui/api/contracts/hiring-jobs";
import type { StageKind } from "@excellent/ui/api/contracts/hiring-pipelines";
import {
  hiringViews,
  type JobViewShape,
  type ViewNoteShape,
} from "@excellent/ui/api/contracts/hiring-views";
import { HiringScopeActions } from "../database/hiring-scope-actions";
import type { ObjectKindRow } from "../database/objects-index-client";

const STATUS_FILTERS = ["draft", "open", "closed"] as const;
const STAGE_FILTERS = ["lead", "active", "offer", "hired", "rejected"] as const;

type SP = Record<string, string | string[] | undefined>;
function flat(sp: SP, k: string): string | undefined {
  const v = sp[k];
  return Array.isArray(v) ? v[0] : v;
}

// ── WorkRow lifecycle mappings (docs/design/WORK-ROW-SYSTEM.md) ───────────────
// The hiring kinds carry their OWN lifecycles, not the task status vocabulary, so
// each maps onto a WorkPhase for the state ring's shape while the row keeps the
// exact hiring word via the state `label` override — a closed job reads "Closed",
// not the phase's generic "Complete". A job/candidate/application row leads with
// its kind glyph (slot 1) so the ring (slot 2) is free to say where it is.
const JOB_PHASE: Record<JobViewShape["status"], WorkPhase> = {
  draft: "notStarted",
  open: "active",
  closed: "done",
  archived: "cancelled",
};
const STAGE_PHASE: Record<StageKind, WorkPhase> = {
  lead: "notStarted",
  active: "active",
  offer: "nearlyDone",
  hired: "done",
  rejected: "cancelled",
};
const APP_PHASE: Record<string, WorkPhase> = {
  active: "active",
  hired: "done",
  rejected: "cancelled",
  withdrawn: "cancelled",
};

/** Two-letter avatar initials from a person's display name ("Jane Doe" → "JD"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ── The three states a contracted read has that a server render did not ───────
// A server component either rendered or threw. A client view has a pending read,
// a failed read, and a resolved-but-absent record, and each needs an honest
// surface. Kept as three tiny local components so all nine views answer them the
// same way — the alternative (nine ad-hoc ternaries) is how two views end up
// disagreeing about what a 500 looks like.

function ViewLoading() {
  return <p className="type-body text-muted-foreground">Loading…</p>;
}

function ViewError({ message }: { message: string }) {
  return <p className="type-body text-destructive">{message}</p>;
}

/**
 * The ported `notFound()`. Rendered, never thrown, so the surrounding chrome and
 * the back-link survive. `backHref`/`backLabel` are optional because the two hub
 * views have nowhere above them to go.
 */
function ViewNotFound({
  title,
  description,
  backHref,
  backLabel,
}: {
  title: string;
  description: React.ReactNode;
  backHref?: string;
  backLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      {backHref ? (
        <Link href={backHref} variant="muted" className="type-body">
          ← {backLabel}
        </Link>
      ) : null}
      <EmptyState title={title} description={description} />
    </div>
  );
}

/** The note list shared by the candidate and application detail views. */
function NoteList({ notes }: { notes: ViewNoteShape[] }) {
  return (
    <Card>
      <CardContent>
        <ul className="flex flex-col">
          {notes.map((n) => (
            <li
              key={n.id}
              className="flex flex-col gap-1 border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0"
            >
              <p className="whitespace-pre-wrap type-body text-foreground">{n.body}</p>
              <p className="type-body text-muted-foreground">
                {formatDateTime(n.created_at)}
              </p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** The contact-identifier list, identical on both detail views. */
function IdentifierList({
  identifiers,
  showPrimaryBadge,
}: {
  identifiers: Array<{ id: number; kind: string; value: string; is_primary: number }>;
  showPrimaryBadge?: boolean;
}) {
  if (identifiers.length === 0) {
    return <p className="type-body text-muted-foreground">No contact info.</p>;
  }
  return (
    <ul className="space-y-1">
      {identifiers.map((i) => (
        <li key={i.id} className="type-body">
          <span className="text-muted-foreground capitalize">{i.kind}:</span>{" "}
          <span className="text-foreground">{i.value}</span>
          {showPrimaryBadge && i.is_primary ? (
            // The origin painted `type-body` here, forking Badge's own `type-subtext`
            // base at the call site. Layout class only now — see the header.
            <Badge variant="outline" className="ml-2">
              primary
            </Badge>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- Jobs list

export function JobsView({
  basePath,
  searchParams,
}: {
  basePath: string;
  searchParams: SP;
}) {
  const statusParam = flat(searchParams, "status");
  const q = flat(searchParams, "q");
  const status = (STATUS_FILTERS as readonly string[]).includes(statusParam ?? "")
    ? (statusParam as (typeof STATUS_FILTERS)[number])
    : undefined;
  const { data, error, isLoading } = useApi(hiringViews.jobs, {
    query: { status: status as JobStatus | undefined, q },
  });
  const scoped = !basePath.startsWith("/projects/sections");

  // The status-filter pills must stay reachable from the mounted Domain view: at
  // the workspace tier `${basePath}/jobs` (= `/projects/sections/hiring/jobs`) has
  // no route, so route the filter through the modes deep-link, which round-trips
  // (ModePage threads `status` into this same view). Scoped tier keeps its route.
  const filterHref = (s?: string) =>
    scoped
      ? s
        ? `${basePath}/jobs?status=${s}`
        : `${basePath}/jobs`
      : s
        ? `/operations?area=hiring&sub=jobs&status=${s}`
        : `/operations?area=hiring&sub=jobs`;

  const newJobButton = (
    <Button asChild size="icon-tb">
      <RouterLink
        href={`${basePath}/jobs/new`}
        aria-label="New job"
        title="New job"
      >
        <Plus className="size-4" />
      </RouterLink>
    </Button>
  );

  const all = data?.jobs ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Jobs"
        description="Internal job postings. Publish to make them visible publicly."
        toolbar={
          <>
            {/* Issue 19: per-page bar → a button that focuses the dock's scoped
                root search (scope `hiring-jobs`); the view reads `q` from the URL
                and the endpoint filters (listJobs server-side). */}
            <ScopedSearchButton label="Search jobs" />
            {scoped ? newJobButton : null}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant={!status ? "default" : "outline"} size="sm">
          <RouterLink href={filterHref()}>All</RouterLink>
        </Button>
        {STATUS_FILTERS.map((s) => (
          <Button
            asChild
            key={s}
            variant={s === status ? "default" : "outline"}
            size="sm"
            className="font-normal capitalize"
          >
            <RouterLink href={filterHref(s)}>{s}</RouterLink>
          </Button>
        ))}
      </div>

      {error ? <ViewError message={error.message ?? String(error)} /> : null}
      {isLoading && !data ? <ViewLoading /> : null}

      {data && all.length === 0 ? (
        <EmptyState
          title="No jobs yet"
          description="Create your first job to start tracking candidates."
          action={scoped ? newJobButton : undefined}
        />
      ) : null}

      {data && all.length > 0 ? (
        // Canonical WorkRow list: the kind glyph leads, the job's status rides
        // the state ring (its exact word kept via the label override), the
        // department/location/type/visibility meta becomes the inline subtitle,
        // and the applicant tally is the one context chip. Opens the job at
        // /o/<uid> via `href`, the same doorway the hand-rolled Link used.
        <Card className="gap-0 overflow-hidden py-0">
          <CardContent className="px-0">
            <WorkList
              gutter
              rows={all.map((j): WorkRowModel => {
                const count = j.applicant_count;
                const meta = [
                  j.department,
                  j.location,
                  j.employment_type,
                  j.is_public ? "Public" : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return {
                  id: j.id,
                  signifier: { as: "glyph", icon: iconForKind("job"), label: "Job" },
                  state: { as: "phase", phase: JOB_PHASE[j.status], label: titleCase(j.status) },
                  title: j.title,
                  subtitle: meta || undefined,
                  pills: [
                    {
                      icon: iconForKind("candidate"),
                      label: `${count} applicant${count === 1 ? "" : "s"}`,
                      hint: "Applicants",
                    },
                  ],
                  href: objectHref(j.uid, "operations"),
                };
              })}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- New job

export function NewJobView({ basePath }: { basePath: string }) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <p className="type-body text-muted-foreground">
        Job lives as a draft until you publish it.
      </p>
      <JobForm basePath={basePath} />
    </div>
  );
}

// ---------------------------------------------------------------- Job detail

export function JobDetailView({
  basePath,
  slug,
}: {
  basePath: string;
  slug: string;
}) {
  const { data, error, isLoading } = useApi(hiringViews.job, {
    params: { slug },
  });

  if (error) return <ViewError message={error.message ?? String(error)} />;
  if (isLoading || !data) return <ViewLoading />;
  if (!data.job) {
    return (
      <ViewNotFound
        title="No such job"
        description={
          <>
            Nothing in the vault is posted under{" "}
            <span className="font-mono">{slug}</span>.
          </>
        }
        backHref={`${basePath}/jobs`}
        backLabel="Jobs"
      />
    );
  }

  const job = data.job;
  const counts = new Map(data.stageCounts.map((c) => [c.stage_id, c.count]));
  const total = data.total;

  return (
    <div className="flex flex-col gap-4">
      <Link href={`${basePath}/jobs`} variant="muted" className="type-body">
        ← Jobs
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <StatusBadge domain="job" status={job.status} />
            {job.is_public ? <Badge variant="outline">Public</Badge> : null}
          </div>
          <p className="type-body text-muted-foreground">
            {job.department ? job.department : "No department"} ·{" "}
            {job.location ?? "No location"} ·{" "}
            {job.employment_type ?? "Unspecified"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button asChild variant="outline">
            <RouterLink href={`${basePath}/jobs/${job.slug}/edit`}>Edit</RouterLink>
          </Button>
          <Button asChild>
            {/* The drag-stage board round-trips through the modes deep-link →
                ModePage → the ats.jobPipeline DomainView, so it resolves at the
                workspace tier too (the section-deep route 404s there). */}
            <RouterLink href={`/operations?area=hiring&sub=jobs/${job.slug}/pipeline`}>
              Open pipeline ({total})
            </RouterLink>
          </Button>
        </div>
      </div>
      {job.is_public ? (
        <p className="type-body text-muted-foreground">
          Public URL:{" "}
          <Link
            href={`/hiring/jobs/${job.slug}?public=1`}
            className="underline"
            target="_blank"
          >
            /hiring/jobs/{job.slug}
          </Link>
        </p>
      ) : null}

      <JobActionsBar job={job} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="type-title">Pipeline summary</CardTitle>
          <span className="type-body text-muted-foreground">
            {total} candidate{total === 1 ? "" : "s"}
          </span>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {data.stages.map((s) => (
              <div key={s.id} className="flex flex-col gap-0.5 px-3 py-2">
                <div className="type-body text-muted-foreground capitalize">
                  {s.kind}
                </div>
                <div className="type-body text-foreground">{s.name}</div>
                <div className="type-heading text-foreground tabular-nums">
                  {counts.get(s.id) ?? 0}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="type-title">Description</CardTitle>
        </CardHeader>
        <CardContent>
          {job.description ? (
            <pre className="type-body whitespace-pre-wrap font-mono text-foreground">
              {job.description}
            </pre>
          ) : (
            <p className="type-body text-muted-foreground">No description yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- Job edit

export function JobEditView({
  basePath,
  slug,
}: {
  basePath: string;
  slug: string;
}) {
  const { data, error, isLoading } = useApi(hiringViews.job, {
    params: { slug },
  });

  if (error) return <ViewError message={error.message ?? String(error)} />;
  if (isLoading || !data) return <ViewLoading />;
  if (!data.job) {
    return (
      <ViewNotFound
        title="No such job"
        description={
          <>
            Nothing in the vault is posted under{" "}
            <span className="font-mono">{slug}</span>.
          </>
        }
        backHref={`${basePath}/jobs`}
        backLabel="Jobs"
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <p className="type-body text-muted-foreground">{data.job.title}</p>
      <JobForm initial={data.job} basePath={basePath} />
    </div>
  );
}

// ---------------------------------------------------------------- Job pipeline

export function JobPipelineView({
  basePath,
  slug,
}: {
  basePath: string;
  slug: string;
}) {
  const { data, error, isLoading } = useApi(hiringViews.jobPipeline, {
    params: { slug },
  });
  const scoped = !basePath.startsWith("/projects/sections");

  if (error) return <ViewError message={error.message ?? String(error)} />;
  if (isLoading || !data) return <ViewLoading />;
  if (!data.job) {
    return (
      <ViewNotFound
        title="No such job"
        description={
          <>
            Nothing in the vault is posted under{" "}
            <span className="font-mono">{slug}</span>.
          </>
        }
        backHref={`${basePath}/jobs`}
        backLabel="Jobs"
      />
    );
  }

  const job = data.job;
  const { activeStages, rejectedStage, applications: apps } = data;

  const byStage: Record<number, ApplicationWithJoins[]> = {};
  const rejected: ApplicationWithJoins[] = [];
  for (const s of activeStages) byStage[s.id] = [];
  if (rejectedStage) byStage[rejectedStage.id] = [];
  for (const a of apps) {
    if (rejectedStage && a.current_stage_id === rejectedStage.id) {
      rejected.push(a);
      byStage[rejectedStage.id]!.push(a);
    } else if (byStage[a.current_stage_id]) {
      byStage[a.current_stage_id]!.push(a);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Link href={objectHref(job.uid, "operations")} variant="muted" className="type-body">
        ← {job.title}
      </Link>
      <div className="flex items-center justify-between gap-4">
        <p className="type-body text-muted-foreground">
          Drag candidates between stages. Every move is audited.
          {job.is_public ? null : (
            // The origin painted `type-body` here — dropped, see the file header.
            <Badge variant="outline" className="ml-2">
              Not yet public
            </Badge>
          )}
        </p>
        {scoped ? (
          <Button asChild variant="outline">
            <RouterLink href={`${basePath}/jobs/${job.slug}/edit`}>Edit job</RouterLink>
          </Button>
        ) : null}
      </div>

      {apps.length === 0 ? (
        <EmptyState
          title="No candidates yet"
          description={
            job.is_public ? (
              <>
                Share the public URL{" "}
                <Link href={`/hiring/jobs/${job.slug}?public=1`} variant="inline">
                  /hiring/jobs/{job.slug}
                </Link>{" "}
                to start collecting applications.
              </>
            ) : (
              <>
                Publish the job to start collecting applications, or add a
                candidate manually from the{" "}
                <Link href="/operations?area=hiring&sub=candidates" variant="inline">
                  Candidates
                </Link>{" "}
                page.
              </>
            )
          }
        />
      ) : (
        <HiringPipelineBoard
          basePath={basePath}
          stages={activeStages}
          byStage={byStage}
          rejectedStage={rejectedStage}
          rejected={rejected}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Candidates

export function CandidatesView({
  basePath,
  searchParams,
  rawRows = [],
}: {
  basePath: string;
  searchParams: SP;
  /**
   * The Hiring-scoped `person` inventory for the Raw-data panel.
   *
   * The origin computed this with `buildObjectRowsForKinds(HIRING_KINDS)` — a
   * vault read. It stays a PROP because the `objects.list` Operation that would
   * replace it does not exist: shared-render.md §5 records it as owed by RO-3-11,
   * and `HiringScopeActions` was ported at RO-3-00 taking `rawRows` for the same
   * reason. Defaults to `[]` — an empty Raw-data cut, which is honest — rather
   * than a `fetch()`, which would be a rail violation.
   */
  rawRows?: ObjectKindRow[];
}) {
  const q = flat(searchParams, "q");
  const stageParam = flat(searchParams, "stage");
  const stage = (STAGE_FILTERS as readonly string[]).includes(stageParam ?? "")
    ? (stageParam as (typeof STAGE_FILTERS)[number])
    : undefined;
  const { data, error, isLoading } = useApi(hiringViews.candidates, {
    query: { q, stage: stage as StageKind | undefined },
  });
  const scoped = !basePath.startsWith("/projects/sections");
  const candidates = data?.candidates ?? [];

  // The stage-filter pills must stay reachable from the mounted Domain view: at
  // the workspace tier `${basePath}/candidates` has no route, so route the filter
  // through the modes deep-link (ModePage threads `stage`/`q` into this same view).
  const filterHref = (s?: string) => {
    const usp = new URLSearchParams();
    if (!scoped) {
      usp.set("area", "hiring");
      usp.set("sub", "candidates");
    }
    if (s) usp.set("stage", s);
    if (q) usp.set("q", q);
    const qs = usp.toString();
    if (scoped) return qs ? `${basePath}/candidates?${qs}` : `${basePath}/candidates`;
    return `/hiring?${qs}`;
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Candidates"
        description="Everyone who has applied or been added to a hiring pipeline."
        toolbar={
          <>
            {/* Issue 19: per-page bar → a button that focuses the dock's scoped
                root search (scope `hiring-candidates`); the view reads `q` from
                the URL and the endpoint filters (listCandidates server-side). */}
            <ScopedSearchButton label="Search candidates" />
            {/* Issue 20 (#43439): Import + Raw-data, scoped to Hiring's `person`
                kind — the page is a dashboard over its own database. Bare
                Fragment (not SectionActions), matching JobsView's toolbar just
                above — PageHeader's toolbar slot already supplies the
                flex/gap layout. */}
            <HiringScopeActions rawRows={rawRows} />
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant={!stage ? "default" : "outline"} size="sm">
          <RouterLink href={filterHref()}>All</RouterLink>
        </Button>
        {STAGE_FILTERS.map((s) => (
          <Button
            asChild
            key={s}
            variant={s === stage ? "default" : "outline"}
            size="sm"
            className="font-normal capitalize"
          >
            <RouterLink href={filterHref(s)}>{s}</RouterLink>
          </Button>
        ))}
      </div>

      {error ? <ViewError message={error.message ?? String(error)} /> : null}
      {isLoading && !data ? <ViewLoading /> : null}

      {data && candidates.length === 0 ? (
        <EmptyState
          title="No candidates yet"
          description={
            q
              ? `Nothing matches "${q}".`
              : "Publish a job and share its public URL to start collecting applications."
          }
        />
      ) : null}

      {data && candidates.length > 0 ? (
        // Candidates ARE people: the person is the row's owner avatar (a face on
        // every row), the email is the inline subtitle, the role they're up for
        // is the one context chip, and the pipeline stage rides the state ring
        // (its real stage name kept via the label override). A candidate with no
        // application shows no stage/role — the absent pipeline marks say so. The
        // old total/active application count is dropped: WorkRow's single-signal
        // band has no honest slot for a bare pair, and a meter would misread the
        // ratio as progress.
        <Card className="gap-0 overflow-hidden py-0">
          <CardContent className="px-0">
            <WorkList
              gutter
              rows={candidates.map((c): WorkRowModel => ({
                id: c.person_id,
                signifier: { as: "glyph", icon: iconForKind("candidate"), label: "Candidate" },
                state: c.latest_stage_kind
                  ? {
                      as: "phase",
                      phase: STAGE_PHASE[c.latest_stage_kind],
                      label: c.latest_stage_name ?? undefined,
                    }
                  : undefined,
                title: c.display_name,
                subtitle: c.primary_email ?? "no email on file",
                pills: c.latest_job_title
                  ? [{ icon: iconForKind("job"), label: c.latest_job_title, hint: "Job" }]
                  : undefined,
                owner: {
                  initials: initialsOf(c.display_name),
                  name: c.display_name,
                  role: "Candidate",
                },
                href: objectHref(c.uid, "operations"),
              }))}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Candidate detail

export function CandidateDetailView({
  basePath,
  id,
}: {
  basePath: string;
  id: string;
}) {
  const personId = Number(id);
  const { data, error, isLoading } = useApi(hiringViews.candidate, {
    params: { id: personId },
  });

  if (error) return <ViewError message={error.message ?? String(error)} />;
  if (isLoading || !data) return <ViewLoading />;
  if (!data.person) {
    return (
      <ViewNotFound
        title="No such candidate"
        description="That person is not in this workspace's directory."
        backHref={`${basePath}/candidates`}
        backLabel="Candidates"
      />
    );
  }

  const { person, identifiers, attachments, applications: apps, syncLink, notes, tags } =
    data;

  return (
    <div className="flex flex-col gap-4">
      <Link href={`${basePath}/candidates`} variant="muted" className="type-body">
        ← Candidates
      </Link>
      <div className="flex items-center gap-2">
        <p className="type-body text-muted-foreground">
          In directory since {formatDate(person.created_at)}
        </p>
        {syncLink && (
          <SyncBadge source={syncLink.source} lastSyncedAt={syncLink.lastSyncedAt} />
        )}
      </div>
      {tags.length > 0 ? (
        <div className="flex gap-2">
          {tags.map((t) => (
            <Badge key={t.id} variant="outline">
              {t.name}
            </Badge>
          ))}
        </div>
      ) : null}

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="type-title">Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <IdentifierList identifiers={identifiers} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="type-title">Attachments</CardTitle>
          </CardHeader>
          <CardContent>
            {attachments.length === 0 ? (
              <p className="type-body text-muted-foreground">No resumes / files.</p>
            ) : (
              <ul className="space-y-1">
                {attachments.map((a) => (
                  <li key={a.id} className="type-body">
                    <a
                      href={`/hiring/api/attachments/${a.id}`}
                      target="_blank"
                      rel="noopener"
                      className="underline"
                    >
                      {a.filename}
                    </a>
                    <span className="text-muted-foreground ml-2">
                      {a.mime_type ?? "unknown"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="type-title">Pipeline activity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="type-body text-muted-foreground">
              {apps.length} application{apps.length === 1 ? "" : "s"} ·{" "}
              {apps.filter((a) => a.status === "active").length} active
            </p>
          </CardContent>
        </Card>
      </section>

      <section>
        <SectionHeading className="mb-2" title="Applications" />
        {apps.length === 0 ? (
          <p className="type-body text-muted-foreground">
            No applications. Use{" "}
            <Link href={`${basePath}/jobs`} className="underline">
              jobs
            </Link>{" "}
            to attach this person to a role.
          </p>
        ) : (
          // Application rows: the kind glyph leads, the application status rides
          // the state ring (its word kept via the label override), the pipeline
          // stage is the one context chip, the applied date takes the date slot
          // ("Applied · …"), and the source rides the subtitle. WorkRow shows one
          // context chip, so the secondary scorecard-rating badge is dropped here
          // (it stays in full on the application detail page's Scorecards block).
          //
          // The per-row job title and stage name arrive ON the row: the origin
          // called `getJobById`/`getStageById` inside this map — an N+1 vault read
          // in the render path — and the endpoint resolves both in one join.
          <Card className="gap-0 overflow-hidden py-0">
            <CardContent className="px-0">
              <WorkList
                gutter
                rows={apps.map((a): WorkRowModel => ({
                  id: a.id,
                  signifier: { as: "glyph", icon: iconForKind("application"), label: "Application" },
                  state: { as: "phase", phase: APP_PHASE[a.status] ?? "active", label: titleCase(a.status) },
                  title: a.job_title ?? `Job ${a.job_id}`,
                  subtitle: `${a.source ?? "manual"}${a.source_detail ? ` · ${a.source_detail}` : ""}`,
                  pills: a.stage_name ? [{ label: a.stage_name, hint: "Stage" }] : undefined,
                  date: {
                    text: formatDayMonth(a.applied_at),
                    hint: `Applied · ${formatDate(a.applied_at)}`,
                  },
                  href: objectHref(a.uid, "operations"),
                }))}
              />
            </CardContent>
          </Card>
        )}
      </section>

      <section>
        <SectionHeading className="mb-2" title="Notes" />
        {notes.length === 0 ? (
          <EmptyState
            variant="inline"
            title="No notes yet"
            description="Add the first interview note from an application detail page."
          />
        ) : (
          <NoteList notes={notes} />
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- Application detail

export function ApplicationDetailView({
  basePath,
  id,
}: {
  basePath: string;
  id: string;
}) {
  const { data, error, isLoading } = useApi(hiringViews.application, {
    params: { id: Number(id) },
  });

  if (error) return <ViewError message={error.message ?? String(error)} />;
  if (isLoading || !data) return <ViewLoading />;
  if (!data.application || !data.job || !data.person) {
    return (
      <ViewNotFound
        title="No such application"
        description="That application is not in this workspace, or its job or candidate has been removed."
        backHref={`${basePath}/jobs`}
        backLabel="Jobs"
      />
    );
  }

  const {
    application: app,
    job,
    person,
    identifiers,
    attachments,
    stages,
    rejectedStage,
    currentStage,
    history,
    scorecards,
    templates,
    notes,
    personNotes,
  } = data;

  const stageMap = new Map(
    (rejectedStage ? [...stages, rejectedStage] : stages).map((s) => [s.id, s]),
  );

  return (
    <div className="flex flex-col gap-4">
      <Link
        href={`${basePath}/jobs/${job.slug}/pipeline`}
        variant="muted"
        className="type-body"
      >
        ← {job.title} pipeline
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2 type-body text-muted-foreground">
          <span>Applied {formatDate(app.applied_at)}</span>
          <span>·</span>
          <span>Source: {app.source ?? "manual"}</span>
          {app.source_detail ? <span>· {app.source_detail}</span> : null}
          <span>·</span>
          <StatusBadge domain="application" status={app.status} />
          {currentStage ? (
            <Badge variant="outline" className="capitalize">
              {currentStage.name}
            </Badge>
          ) : null}
        </div>
        <Link
          href={`${basePath}/candidates/${person.id}`}
          variant="muted"
          className="shrink-0 type-body"
        >
          Full candidate profile →
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="type-title">Move candidate</CardTitle>
        </CardHeader>
        <CardContent>
          <ApplicationActions
            applicationId={app.id}
            currentStageId={app.current_stage_id}
            stages={stages}
            rejectedStage={rejectedStage}
            status={app.status}
          />
          {app.rejection_reason ? (
            <p className="type-body text-muted-foreground mt-2">
              Rejection reason: {app.rejection_reason}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="type-title">Contact</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <IdentifierList identifiers={identifiers} showPrimaryBadge />
          {attachments.length > 0 ? (
            <div className="mt-2">
              <SectionHeading as="h3" title="Attachments" />
              <ul className="mt-1 space-y-1">
                {attachments.map((a) => (
                  <li key={a.id} className="type-body">
                    <a
                      href={`/hiring/api/attachments/${a.id}`}
                      target="_blank"
                      rel="noopener"
                      className="underline"
                    >
                      {a.filename}
                    </a>
                    <span className="text-muted-foreground ml-2">
                      {a.mime_type ?? "unknown"} ·{" "}
                      {a.byte_size
                        ? `${formatNumber(a.byte_size / 1024, { maxFractionDigits: 1 })} KB`
                        : "?"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="type-title">Stage history</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2">
            {history.map((h) => {
              const s = stageMap.get(h.stage_id);
              return (
                <li
                  key={h.id}
                  className="flex items-center justify-between gap-3 border-b border-border/40 py-2 type-body first:pt-0 last:border-b-0 last:pb-0"
                >
                  <div>
                    <Badge variant="outline" className="capitalize">
                      {s?.name ?? `stage ${h.stage_id}`}
                    </Badge>
                    {h.note ? (
                      <span className="ml-2 text-muted-foreground">{h.note}</span>
                    ) : null}
                  </div>
                  <div className="text-right type-body text-muted-foreground">
                    <div>Entered: {formatDate(h.entered_at)}</div>
                    <div>Exited: {h.exited_at ? formatDate(h.exited_at) : "—"}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <SectionHeading title={`Scorecards (${scorecards.length})`} />
          {scorecards.length === 0 ? (
            <p className="type-body text-muted-foreground">No scorecards yet.</p>
          ) : (
            scorecards.map((sc) => <ScorecardSummary key={sc.id} scorecard={sc} />)
          )}
          <ScorecardForm
            applicationId={app.id}
            templates={templates}
            settingsHref={`${basePath}/settings`}
          />
        </div>

        <div className="space-y-3">
          <SectionHeading title="Notes" />
          <Card>
            <CardContent>
              <NoteAddForm applicationId={app.id} />
            </CardContent>
          </Card>
          {notes.length === 0 ? (
            <EmptyState
              variant="inline"
              title="No notes yet"
              description="Capture the first thought above."
            />
          ) : (
            <NoteList notes={notes} />
          )}
          {personNotes.length > 0 ? (
            <>
              <SectionHeading
                as="h3"
                className="mt-4"
                title="Notes on this person (across all applications)"
              />
              <NoteList notes={personNotes} />
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- Pipeline hub

export function PipelineHubView({ basePath }: { basePath: string }) {
  // `basePath` is unread here, exactly as in the origin — every link in this view
  // goes through the modes deep-link so it resolves at the workspace tier too.
  // Kept in the signature for call-site parity with the other eight views.
  void basePath;
  const { data, error, isLoading } = useApi(hiringViews.pipelineHub, {});
  const open = data?.jobs ?? [];

  return (
    <div className="flex flex-col gap-6">
      <p className="type-body text-muted-foreground">
        Pick a job to see its kanban. Pipelines are scoped per-job in v1.
      </p>
      {/* Simulation doorway (§6.5.7 harness): hiring-demand what-if — project the
          fulfilment impact of a change in application volume. Its two ops are
          among the five RO-1-16 owes a mount for (shared-render.md §6), so the
          projection 404s until then — contract E in `../index.ts`. */}
      <SimulateInline
        scenario="inflow"
        kind="application"
        prompt="What if applicant volume changes? Project the fulfilment impact of more (or fewer) applications."
        leverLabel="Application inflow change %"
      />

      {error ? <ViewError message={error.message ?? String(error)} /> : null}
      {isLoading && !data ? <ViewLoading /> : null}

      {data && open.length === 0 ? (
        <EmptyState
          title="No open jobs"
          description="Open a job to start receiving applications."
          action={
            <Button variant="outline" size="sm" asChild>
              {/* Jobs is a mounted Domain view — link via the modes deep-link so
                  it resolves at the workspace tier (the section route 404s there). */}
              <RouterLink href="/operations?area=hiring&sub=jobs">Manage jobs</RouterLink>
            </Button>
          }
        />
      ) : null}

      {data && open.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {open.map((j) => (
            <li key={j.id}>
              <Card interactive asChild>
                <RouterLink href={`/operations?area=hiring&sub=jobs/${j.slug}/pipeline`}>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="type-body text-foreground">{j.title}</span>
                      <span className="type-body text-muted-foreground">
                        {j.total} candidate{j.total === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="mt-1 type-body text-muted-foreground">
                      {j.department ?? "No department"} · {j.location ?? "No location"}
                    </div>
                  </CardContent>
                </RouterLink>
              </Card>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Settings

export function HiringSettingsView() {
  const { data, error, isLoading } = useApi(hiringViews.settings, {});

  // Single grouped, single-scroll page with a sticky right "On this page" rail
  // (the shared OnThisPageNav) — mirrors the CRM back-office rollout. Each block
  // renders its own Card(s); the rail anchors to the `<section id>` wrappers.
  const groups: OnThisPageGroup[] = [
    {
      label: "Hiring process",
      items: [
        { id: "pipelines", label: "Pipelines" },
        { id: "scorecards", label: "Scorecards" },
        { id: "sources", label: "Application sources" },
      ],
    },
    { label: "Connections", items: [{ id: "integrations", label: "Integrations" }] },
  ];

  if (error) return <ViewError message={error.message ?? String(error)} />;
  if (isLoading || !data) return <ViewLoading />;

  const detectedProvider = data.detectedProvider;

  return (
    <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-12">
      <div className="flex min-w-0 flex-1 flex-col gap-8">
        <section id="pipelines" className="scroll-mt-6 flex flex-col gap-3">
          <SectionHeading
            as="h3"
            title="Pipelines"
            description="Define the stages candidates move through, from application to hire."
          />
          <PipelineSettings pipelines={data.pipelines} />
        </section>

        <section id="scorecards" className="scroll-mt-6 flex flex-col gap-3">
          <SectionHeading
            as="h3"
            title="Scorecards"
            description="Interview question templates. Assign scorecards to pipeline stages to capture structured feedback."
          />
          <ScorecardTemplateSettings templates={data.templates} />
        </section>

        <section id="sources" className="scroll-mt-6 flex flex-col gap-3">
          <SectionHeading
            as="h3"
            title="Application sources"
            description="Where applications originate. Used as a default for new candidates."
          />
          <SourceSettings sources={data.sources} />
        </section>

        <section id="integrations" className="scroll-mt-6 flex flex-col gap-3">
          {/* ProductIntegrationsPanel self-titles "Integrations" + describes itself. */}
          {detectedProvider ? (
            <p className="type-body text-muted-foreground">
              Detected from your careers page:{" "}
              <span className="text-foreground">{detectedProvider.label}</span>
              {detectedProvider.connectionCount === 0
                ? " — click the card below to connect."
                : " — already connected."}
            </p>
          ) : null}
          {/* `productLabel` crosses the WIRE, not the import graph — the origin's
              `import { HIRING_DISPLAY_NAME } from "@excellent/core"` is the exact
              barrel edge client/vite.config.ts measured dragging the SQLCipher
              keystore path into the browser bundle. */}
          <ProductIntegrationsPanel
            productLabel={data.productLabel}
            providers={data.providers}
          />
        </section>
      </div>

      <OnThisPageNav
        groups={groups}
        className="hidden shrink-0 lg:sticky lg:top-6 lg:block lg:w-56 lg:self-start"
      />
    </div>
  );
}
